import { useCallback, useEffect, useRef } from 'react'

import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { paymentCancelledEvent } from 'src/Helpers/TagManager'
import { getUserSelector } from 'src/Redux/auth-process/userSlice/selectors'
import { getPaymentMethod } from 'src/Redux/payment-process/selectors'
import { getAllCampaignsCount, useMetadataQuery } from 'src/Redux/metadataApi'
import { CampaignPlatformTypeEnum, CampaignTypeEnum } from 'src/Types/campaigns'
import { usePaymentMethod } from 'src/Helpers/usePaymentMethod'

import { useResetQueryParams } from '../Helpers/useResetQueryParams'

import { useCustomSearchParams } from './useCustomSearchParams'

interface PaymentCancelledEvents {
  handlePaymentCancelledAdsEvent: (params: { platformName: string }) => void
}

export const usePaymentCancelledAdsEvents = (): PaymentCancelledEvents => {
  const { data } = useMetadataQuery(null)
  const param = useParams()
  const campaignId = Number(param.number) || 0
  const user = useSelector(getUserSelector)
  const paymentMethod = useSelector(getPaymentMethod)
  const amountOfCampaigns = getAllCampaignsCount(data)
  const { search } = useCustomSearchParams()
  const paymentCancelled = search.get('payment')
  const priceFromQuery = Number(search.get('price'))
  const isPayPalToken = search.get('token')
  const isPayPalSaved = search.get('isPayPalSaved')

  const initialized = useRef(false)

  const resetQueryParams = useResetQueryParams([
    'payment',
    'token',
    'price',
    'isPayPalSaved',
  ])

  const paymentMethodFromFunction = usePaymentMethod(
    isPayPalToken,
    isPayPalSaved,
    paymentMethod,
  )

  const handlePaymentCancelledAdsEvent = useCallback(
    async (params: { platformName: string }): Promise<void> => {
      paymentCancelledEvent({
        campaignId,
        paymentMethod: paymentMethodFromFunction,
        genresList: '',
        userId: user.id,
        userRole: user.role,
        campaignNumber: amountOfCampaigns,
        platformName: params.platformName,
        campaignType: CampaignTypeEnum.BASIC,
        campaignValue: priceFromQuery ?? 0,
        paymentAmount: priceFromQuery ?? 0,
      })

      resetQueryParams()
    },
    [
      campaignId,
      paymentMethodFromFunction,
      user.id,
      user.role,
      amountOfCampaigns,
      priceFromQuery,
      resetQueryParams,
    ],
  )

  useEffect(() => {
    if (paymentCancelled === 'cancelled' && !initialized.current) {
      initialized.current = true

      void handlePaymentCancelledAdsEvent({
        platformName: CampaignPlatformTypeEnum.META,
      })
    }
  }, [handlePaymentCancelledAdsEvent, paymentCancelled])

  return {
    handlePaymentCancelledAdsEvent,
  }
}
